import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import './StatsBlock.css';

/* ─── Data ────────────────────────────────────────── */
const STATS = [
  {
    id: 'speed',
    prefix: '<',
    value: 200,
    suffix: 'ms',
    label: { en: 'Execution Speed', es: 'Velocidad de Ejecución' },
    accent: '#26E2B3',
    headline: { en: 'Fastest NFT execution\non Hedera.', es: 'La ejecución NFT\nmás rápida en Hedera.' },
    body: {
      en: 'W·Sniper fires in under 200 milliseconds across SentX, Kabila and all major Hedera marketplaces. Every millisecond matters when competing for rare NFTs.',
      es: 'W·Sniper ejecuta en menos de 200ms en SentX, Kabila y todos los marketplaces de Hedera. Cada milisegundo cuenta cuando compites por NFTs raros.',
    },
  },
  {
    id: 'tools',
    prefix: '',
    value: 7,
    suffix: '',
    label: { en: 'Tools Live', es: 'Herramientas Activas' },
    accent: '#a78bfa',
    headline: { en: '7 specialized tools\nin one ecosystem.', es: '7 herramientas especializadas\nen un ecosistema.' },
    body: {
      en: 'Sniper, Scanner, Trader, Panel, Claimer, Checker, and Boost. Each tool designed to solve a specific Web3 problem, all working together.',
      es: 'Sniper, Scanner, Trader, Panel, Claimer, Checker y Boost. Cada herramienta diseñada para resolver un problema Web3 específico, todas trabajando juntas.',
    },
  },
  {
    id: 'chains',
    prefix: '',
    value: 5,
    suffix: '',
    label: { en: 'Chains Supported', es: 'Cadenas Soportadas' },
    accent: '#fbbf24',
    headline: { en: 'One dashboard,\nmultiple chains.', es: 'Un dashboard,\nmúltiples cadenas.' },
    body: {
      en: 'Hedera live today. Solana, Base, Ethereum and Arbitrum coming soon. Manage all your assets from a single interface.',
      es: 'Hedera activo hoy. Solana, Base, Ethereum y Arbitrum próximamente. Gestiona todos tus activos desde una sola interfaz.',
    },
  },
  {
    id: 'custody',
    prefix: '',
    value: 100,
    suffix: '%',
    label: { en: 'Self-custody', es: 'Auto-custodia' },
    accent: '#60a5fa',
    headline: { en: 'Your keys,\nyour assets.', es: 'Tus llaves,\ntus activos.' },
    body: {
      en: 'We never touch your private keys. Connect your wallet, sign transactions, stay in control. No custodial risks, no trusted third parties.',
      es: 'Nunca tocamos tus claves privadas. Conecta tu wallet, firma transacciones, mantén el control. Sin riesgos de custodia, sin terceros de confianza.',
    },
  },
];

/* ─── Main ────────────────────────────────────────── */
const StatsBlock = () => {
  const { language } = useLanguage();
  const isEN = language === 'en';
  const [active, setActive] = useState(0);
  const [inView, setInView]   = useState(false);
  const [displayed, setDisplayed] = useState(0); // animated counter value
  const rafRef = useRef(null);
  const sectionRef = useRef(null);

  /* IntersectionObserver to trigger on scroll */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.25 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  /* Animate counter whenever active or inView changes */
  useEffect(() => {
    if (!inView) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const target = STATS[active].value;
    let startTime = null;
    const duration = 1400;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayed(Math.floor(eased * target));
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [active, inView]);

  const stat = STATS[active];
  const displayNum = inView
    ? `${stat.prefix}${displayed.toLocaleString()}${stat.suffix}`
    : `${stat.prefix}0${stat.suffix}`;

  const prev = () => setActive(a => (a - 1 + STATS.length) % STATS.length);
  const next = () => setActive(a => (a + 1) % STATS.length);

  return (
    <section className="sb-section" ref={sectionRef} id="stats">
      <div className="sb-inner container">

        {/* ── Left: text + tabs ─── */}
        <motion.div
          className="sb-left"
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.35 }}
        >
          <div className="sb-eyebrow">
            <span className="sb-eyebrow-dot" />
            {isEN ? 'by the numbers' : 'en números'}
          </div>

          <h2 className="sb-title">
            {isEN
              ? <><span className="sb-title-accent">Web3 tools</span><br />that perform.</>
              : <><span className="sb-title-accent">Herramientas Web3</span><br />que rinden.</>
            }
          </h2>

          <p className="sb-desc">
            {isEN
              ? 'Real numbers from production. No estimates, no projections.'
              : 'Números reales de producción. Sin estimaciones, sin proyecciones.'
            }
          </p>

          {/* Stat selector tabs */}
          <div className="sb-tabs">
            {STATS.map((s, i) => (
              <button
                key={s.id}
                className={`sb-tab ${i === active ? 'sb-tab--active' : ''}`}
                onClick={() => setActive(i)}
                style={{ '--accent': s.accent }}
              >
                <span className="sb-tab-num">
                  {`${s.prefix}${s.value.toLocaleString()}${s.suffix}`}
                </span>
                <span className="sb-tab-label">
                  {s.label[language] || s.label.en}
                </span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* ── Right: big stat card ─── */}
        <motion.div
          className="sb-right"
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.35, delay: 0.12 }}
        >
          <div className="sb-card" style={{ '--accent': stat.accent }}>

            {/* Navigation arrows */}
            <div className="sb-card-nav">
              <button className="sb-arrow" onClick={prev}><ChevronLeft size={15} /></button>
              <button className="sb-arrow" onClick={next}><ChevronRight size={15} /></button>
            </div>

            {/* Metric label */}
            <div className="sb-card-label">
              {stat.label[language] || stat.label.en}
            </div>

            {/* Giant animated number */}
            <div className="sb-big-num" style={{ color: stat.accent }}>
              {displayNum}
            </div>

            {/* Headline */}
            <AnimatePresence mode="wait">
              <motion.h3
                key={`h${active}`}
                className="sb-card-headline"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.28 }}
              >
                {(stat.headline[language] || stat.headline.en).split('\n').map((line, i) => (
                  <span key={i} className="sb-hl-line">{line}</span>
                ))}
              </motion.h3>
            </AnimatePresence>

            {/* Body text */}
            <AnimatePresence mode="wait">
              <motion.p
                key={`b${active}`}
                className="sb-card-body"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.28, delay: 0.08 }}
              >
                {stat.body[language] || stat.body.en}
              </motion.p>
            </AnimatePresence>

            {/* Dot indicators */}
            <div className="sb-card-dots">
              {STATS.map((s, i) => (
                <button
                  key={s.id}
                  className={`sb-dot ${i === active ? 'sb-dot--active' : ''}`}
                  style={{ '--acc': s.accent }}
                  onClick={() => setActive(i)}
                  aria-label={s.label.en}
                />
              ))}
            </div>

          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default StatsBlock;
